import { FormGroup, Input, Label, SubmitButton, Switch } from "@xola/ui-kit";
import {
    FormField,
    InputField,
    requiredNumberAtleastZero,
    requiredNumberMoreThanZero,
    requiredString,
    SelectField,
} from "common";
import React from "react";
import {
    AggregatorObject,
    Coupon,
    CouponType,
    CouponTypeCopy,
    Permission,
    PurchaseRuleAction,
    PurchaseRuleActionObject,
    PurchaseRuleModifier,
    PurchaseRuleModifierOperation,
    VoucherCoupon,
    VoucherPaymentMethod,
    VoucherPaymentMethodCopy,
} from "types";
import { boolean, object, ref } from "yup";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { CurrencyInputField } from "../Field/CurrencyInputField";
import { TagInputField } from "../Field/TagInputField";
import { ContentLayout } from "../Layout/ContentLayout";
import { CouponEditorProps } from "./CouponEditorProps";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useAuthorization } from "@/hooks/useAuthorization";

const validationSchema = object().shape({
    name: requiredString(),
    amount: requiredNumberMoreThanZero(),
    purchaseAmount: requiredNumberAtleastZero().max(ref("amount"), "must be less than or equal to promotional value"),
    maxDemographics: requiredNumberMoreThanZero(),
    paymentMethod: requiredString(),
    excludeFee: boolean(),
});

const getInitialValues = (coupon: Coupon): VoucherCoupon => {
    if (coupon.id) {
        return coupon as VoucherCoupon;
    }

    return {
        ...(coupon as VoucherCoupon),
        excludeTaxFee: false,
        type: CouponType.VOUCHER,
    };
};

export const VoucherCouponEditor = ({ coupon, isSaving, onSubmit, onCancel }: CouponEditorProps) => {
    const handleSubmit = async (coupon: VoucherCoupon) => {
        onSubmit({ coupon: getCoupon(coupon) });
    };

    const { isGranted } = useAuthorization();
    const isMerchandiseAvailable = isGranted(Permission.MERCHANDISE_MODIFY);

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange
            validateOnBlur={false}
            validationSchema={validationSchema}
            initialValues={getInitialValues(coupon)}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, values, setFieldValue }) => (
                <ContentLayout isWide isDisabled={!isGranted(Permission.MODIFY_COUPONS) || isSaving}>
                    <ContentLayout.Section
                        title="Voucher"
                        description="Vouchers are similar to coupons, the key difference being that vouchers are applied as a payment method while other coupon types are applied as a discount."
                    >
                        <FormGroup>
                            <Label>Type</Label>
                            <Input disabled value={CouponTypeCopy.voucher} />
                        </FormGroup>

                        <FormField name="name" label="Campaign Name">
                            <InputField isRequired placeholder="Campaign Name" />
                        </FormField>

                        <FormField
                            name="amount"
                            label="Promotional Value"
                            tooltip="This is the maximum total amount that can be redeemed when using a code from this campaign for a booking that falls within your campaign's restrictions."
                        >
                            <CurrencyInputField filterZero isRequired />
                        </FormField>

                        <FormField
                            name="purchaseAmount"
                            label="Purchase Value"
                            tooltip="This is the amount paid by your customer when they purchased this voucher deal. This is also the maximum total amount that can be redeemed when using a code from this campaign for a booking that violates your campaign's restrictions."
                        >
                            <CurrencyInputField isRequired />
                        </FormField>

                        <FormField
                            name="maxDemographics"
                            label={
                                isMerchandiseAvailable
                                    ? "Number of purchasable items per code"
                                    : "Number of demographics per code"
                            }
                            tooltip={
                                isMerchandiseAvailable
                                    ? "This is the number of purchasable items (experience demographics, memberships, merchandise items, etc.) that will receive a promotional price when booked from this campaign. Each item will share the campaign's Promotional Value equally. For example, if your campaign has a Promotional Value of $100 and the number of purchasable items per code is 4, then up to 4 items will receive a $25 discount when using a code from this campaign."
                                    : "This is the number of demographics that will receive a promotional price when booked using a code from this campaign. Each demographic will share the campaign's Promotional value equally. For example, if your campaign has Promotional Value of $100 and Number of demographics per code of 4, then up to 4 people will receive a $25 discount when using a code from this campaign."
                            }
                        >
                            <InputField isRequired type="number" />
                        </FormField>

                        <FormField name="tags" label="Booking Tags">
                            <TagInputField />
                        </FormField>

                        <div className="flex items-center space-x-4">
                            <FormField name="paymentMethod" label="Reported payment method" className="w-56">
                                <SelectField>
                                    <option value={VoucherPaymentMethod.OTHER}>
                                        {VoucherPaymentMethodCopy[VoucherPaymentMethod.OTHER]}
                                    </option>

                                    <option value={VoucherPaymentMethod.GROUPON}>
                                        {VoucherPaymentMethodCopy[VoucherPaymentMethod.GROUPON]}
                                    </option>

                                    <option value={VoucherPaymentMethod.LIVING_SOCIAL}>
                                        {VoucherPaymentMethodCopy[VoucherPaymentMethod.LIVING_SOCIAL]}
                                    </option>
                                </SelectField>
                            </FormField>

                            {[VoucherPaymentMethod.GROUPON, VoucherPaymentMethod.LIVING_SOCIAL].includes(
                                values.paymentMethod as VoucherPaymentMethod,
                            ) ? null : (
                                <FormField name="paymentMethod" label="Name" className="flex-grow">
                                    <InputField isRequired />
                                </FormField>
                            )}
                        </div>

                        <div>
                            <Switch.Group>
                                <Switch
                                    isChecked={values.excludeTaxFee}
                                    onChange={(value: boolean) => {
                                        setFieldValue("excludeTaxFee", value);
                                    }}
                                />

                                <Switch.Label direction="right" className="text-gray-darker">
                                    Remove taxes and fees
                                </Switch.Label>
                            </Switch.Group>

                            <div className="pl-10 text-sm text-gray-dark">
                                If enabled, all taxes and fees will be removed from a booking when a code from this
                                campaign is applied
                            </div>
                        </div>
                    </ContentLayout.Section>

                    <ContentLayout.Footer>
                        <SubmitButton type="submit" isLoading={isSaving} onClick={handleSubmit}>
                            {coupon?.id ? "Save" : "Next"}
                        </SubmitButton>

                        <SecondaryButton
                            type="reset"
                            onClick={() => {
                                onCancel();
                            }}
                        >
                            Cancel
                        </SecondaryButton>
                    </ContentLayout.Footer>

                    <ScrollToFieldError />
                </ContentLayout>
            )}
        </XolaFormik>
    );
};

const getCoupon = (coupon: VoucherCoupon): VoucherCoupon => {
    const action = getPurchaseRuleAction(coupon);
    return {
        ...coupon,
        purchaseRule: {
            name: "",
            ...coupon.purchaseRule,
            actions: [action],
            seller: coupon.seller,
        },
    };
};

const getPurchaseRuleAction = (coupon: VoucherCoupon): PurchaseRuleAction => {
    const [action] = coupon.purchaseRule?.actions ?? [];
    let modifiers: PurchaseRuleModifier[] | [] = [];

    if (coupon.amount !== coupon.purchaseAmount) {
        modifiers = [
            {
                field: "amount",
                operation: PurchaseRuleModifierOperation.SET,
                aggregator: {
                    object: AggregatorObject.VOUCHER_DISCOUNT_AGGREGATOR,
                    coupon: { id: coupon.id },
                },
            },
        ];
    }

    return {
        ...action,
        object: PurchaseRuleActionObject.UPDATE_LINE_ITEM_ACTION,
        modifiers: modifiers,
    };
};
